import type { FC } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { Show } from '@components/Show';
import { Table } from '@components/Table';
import { StatusCell } from '@ContractBuilder/contract-builder-header/components/StatusCell';
import type { EntityData } from '@ContractBuilder/types';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { formatDate, sortByDate } from '@root/helpers';
import Nav from '@src/routes';
import { createColumnHelper } from '@tanstack/react-table';

import { CreateRenewalButton } from './CreateRenewalButton';

interface ListRenewalsButtonProps {
  data?: EntityData[];
  isLoading: boolean;
  submission: EntityData;
}
const columnHelper = createColumnHelper<EntityData>();
const columns = [
  columnHelper.accessor('name', {
    id: 'name',
    header: 'Name',
    cell: (info) => {
      const value = info.getValue();
      return (
        <p className="text-info-500 truncate" title={value}>
          {value}
        </p>
      );
    },
  }),
  columnHelper.accessor('updated_at', {
    id: 'updated_at',
    header: 'Updated',
    size: 180,
    cell: (info) => {
      const value = info.getValue() ?? info.row.original.created_at;
      return <p className="text-info-500 whitespace-nowrap">{formatDate(value, 'DD MMM YYYY HH:mm')}</p>;
    },
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    size: 124,
    cell: (info) => {
      return <StatusCell status={info.getValue()} />;
    },
  }),
  columnHelper.accessor('created_at', {
    id: 'created_at',
    header: 'Created',
    size: 136,
    cell: (info) => {
      const value = info.getValue();
      return <p className="text-info-500 whitespace-nowrap">{formatDate(value, 'DD MMM YYYY')}</p>;
    },
  }),
];

export const ListRenewalsButton: FC<ListRenewalsButtonProps> = ({ data = [], isLoading, submission }) => {
  const navigate = useNavigate();

  const getFeatureFlagState = useFeatureFlags();
  const disableRecordCreation = getFeatureFlagState('DisableRecordCreation');

  const handleItemClick = (item: EntityData) => {
    const renewalContractPath = generatePath(Nav.SubmissionContract, {
      id: item.id,
    });

    navigate(renewalContractPath);
  };

  return (
    <div>
      <h2 className="text-info-900 px-4 pt-4 pb-2 text-lg leading-normal">Renewals</h2>
      <div className="max-h-[max(10rem,calc(50vh-10rem))] overflow-y-auto shadow-inner">
        <Table<EntityData>
          className="table-fixed"
          data={sortByDate(data, 'desc', 'updated_at')}
          emptyRowsLabel="No renewals available"
          columns={columns}
          isLoading={isLoading}
          onRowClick={handleItemClick}
          rowsSkeletonLength={(submission.renewals ?? []).length}
        />
      </div>
      <Show when={!disableRecordCreation}>
        <CreateRenewalButton
          className="align-start border-info-200 w-full items-center rounded-tl-none rounded-tr-none border-t border-r-0 border-b-0 border-l-0"
          isDisabled={isLoading}
        />
      </Show>
    </div>
  );
};
